/**
 * Service for handling attachments (drawings and photos)
 * Uses the attachment endpoints:
 * - GET /attach?customerId=id&attachType=type - Get attachments
 * - POST /attach - Create attachment
 * - DELETE /attach/{attachId} - Delete attachment
 */
import axios from 'axios';
import { USER_ENDPOINTS } from '../Constants';

// In-memory cache for attachments to prevent unnecessary API calls
const attachmentsCache = {
    photos: new Map(), // Map of customerId -> photo attachments
    projects: new Map() // Map of customerId -> project attachments
};

export const AttachmentService = {
    /**
     * Get attachments for a customer by type
     * @param {string} customerId - ID of the customer
     * @param {string} attachType - Type of attachment ('PROJECT' or 'PHOTO')
     * @param {boolean} forceRefresh - Whether to bypass cache and force a fresh fetch
     * @returns {Promise<Array>} - Array of attachments
     */
    getAttachments: async (customerId, attachType, forceRefresh = false) => {
        try {
            if (!customerId) {
                console.warn('No customer ID provided to getAttachments');
                return [];
            }

            // Make sure attachType is uppercase to match the enum in the backend
            const formattedAttachType = attachType.toUpperCase();
            const cacheKey = formattedAttachType === 'PHOTO' ? 'photos' : 'projects';

            // Return cached results if available and not forcing refresh
            if (!forceRefresh && attachmentsCache[cacheKey].has(customerId)) {
                console.log(`Using cached ${cacheKey} for customer ${customerId}`);
                return attachmentsCache[cacheKey].get(customerId);
            }

            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('No authentication token found');
            }

            const response = await axios.get(USER_ENDPOINTS.GET_ATTACHMENTS, {
                params: {
                    customerId,
                    attachType: formattedAttachType
                },
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            // Process the response and convert to standardized attachment objects
            const attachments = Array.isArray(response.data) ? response.data.map(attach => ({
                id: attach.id,
                url: attach.fileUrl,
                attachId: attach.id,
                fileName: attach.fileName,
                attachType: formattedAttachType
            })) : [];

            // Update cache
            attachmentsCache[cacheKey].set(customerId, attachments);

            return attachments;
        } catch (error) {
            console.error(`Error fetching ${attachType} attachments:`, error);
            return []; // Return empty array on error instead of throwing
        }
    },

    /**
     * Create a new attachment
     * @param {Object} attachmentData - Attachment data
     * @param {string} attachmentData.customerId - ID of the customer
     * @param {File|Blob|Object} attachmentData.files - File or Blob to upload
     * @param {string} attachmentData.attachType - Type of attachment ('project' or 'photo')
     * @returns {Promise<Object>} - Created attachment details
     */
    createAttachment: async (attachmentData) => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('No authentication token found');
            }

            const formData = new FormData();
            formData.append('customerId', attachmentData.customerId);

            // Handle file data based on its type
            const listName = attachmentData.attachType.toLowerCase() === 'project' ? 'projectList' : 'photoList';

            if (attachmentData.files instanceof File) {
                // If it's a regular File object from file input
                formData.append(listName, attachmentData.files);
            }
            else if (attachmentData.files instanceof Blob) {
                // If it's a plain Blob object
                formData.append(listName, attachmentData.files, 'file.png');
            }
            else if (attachmentData.files && typeof attachmentData.files === 'object') {
                // For canvas drawings or other custom objects with blob property
                const blob = attachmentData.files.blob || attachmentData.files;
                const fileName = attachmentData.files.name || 'drawing.png';
                formData.append(listName, blob, fileName);
            }

            const response = await axios.post(USER_ENDPOINTS.CREATE_ATTACHMENT, formData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            });

            // Clear cache for this customer to ensure fresh data on next fetch
            const cacheKey = attachmentData.attachType.toLowerCase() === 'project' ? 'projects' : 'photos';
            attachmentsCache[cacheKey].delete(attachmentData.customerId);

            return response.data;
        } catch (error) {
            console.error('Error creating attachment:', error);
            throw error;
        }
    },

    /**
     * Delete an attachment by ID
     * @param {string} attachId - ID of the attachment to delete
     * @returns {Promise<Object>} - Response data
     */
    deleteAttachment: async (attachId) => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('No authentication token found');
            }

            // Updated to match the DELETE /{attachId} endpoint pattern
            const response = await axios.delete(`${USER_ENDPOINTS.DELETE_ATTACHMENT}/${attachId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            // NOTE: We don't clear cache here because we don't know which customer this belongs to
            // The cache will be updated on the next fetch or can be manually cleared

            return response.data;
        } catch (error) {
            console.error('Error deleting attachment:', error);
            throw error;
        }
    },

    /**
     * Clear the attachment cache for a specific customer
     * @param {string} customerId - ID of the customer
     * @param {string} attachType - (Optional) Type of attachment ('PROJECT' or 'PHOTO')
     */
    clearCache: (customerId, attachType = null) => {
        if (attachType) {
            const cacheKey = attachType.toUpperCase() === 'PHOTO' ? 'photos' : 'projects';
            attachmentsCache[cacheKey].delete(customerId);
        } else {
            // Clear both caches if no specific type
            attachmentsCache.photos.delete(customerId);
            attachmentsCache.projects.delete(customerId);
        }
    },

    /**
     * Converts a data URL to a Blob object
     * @param {string} dataUrl - The data URL to convert
     * @param {string} filename - Name for the file (optional)
     * @returns {Object} - Object with blob and filename
     */
    dataURLtoBlob: (dataUrl, filename = 'drawing.png') => {
        // Extract mime type and base64 data
        const arr = dataUrl.split(',');
        const mime = arr[0].match(/:(.*?);/)[1];
        const bstr = atob(arr[1]);

        // Convert base64 to binary
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }

        // Create blob
        const blob = new Blob([u8arr], { type: mime });

        // Return blob object
        return blob;
    }
};

export default AttachmentService;